import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  getDaysInMonth,
  startOfMonth,
  getDay,
  eachDayOfInterval,
  startOfYear,
  endOfYear,
  isSameDay,
} from "date-fns";

const actionsColorMap = [
  "#30D158", "#0A84FF", "#FF453A", "#FFD60A",
  "#BF5AF2", "#FF9F0A", "#64D2FF", "#FF375F", "#5E5CE6", "#7CB342",
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function normalizeDay(raw) {
  if (!raw) return null;
  if (raw.hours && typeof raw.hours === "object" && !Array.isArray(raw.hours)) return raw;
  if (Array.isArray(raw)) {
    const hours = {};
    for (const entry of raw) {
      hours[entry.hour] = (entry.items || []).map((item, i) => ({
        ...item,
        colorIndex: i,
      }));
    }
    return { hours };
  }
  return null;
}

function normalizeWeek(raw, weekStartDate) {
  if (!raw) return null;
  if (raw.grid && typeof raw.grid === "object" && !Array.isArray(raw.grid)) return raw;
  if (Array.isArray(raw)) {
    const grid = {};
    for (let d = 0; d < raw.length; d++) {
      const dayDate = addDays(weekStartDate, d);
      const dateKey = format(dayDate, "yyyy-MM-dd");
      grid[dateKey] = {};
      if (Array.isArray(raw[d])) {
        for (let h = 0; h < raw[d].length; h++) {
          if (raw[d][h] > 0) grid[dateKey][h] = raw[d][h];
        }
      }
    }
    return { grid };
  }
  return null;
}

function normalizeMonth(raw, year, month) {
  if (!raw) return null;
  if (raw.days && typeof raw.days === "object" && !Array.isArray(raw.days)) return raw;
  if (Array.isArray(raw)) {
    const days = {};
    for (const entry of raw) {
      const dayNum = entry.day;
      const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      days[dateKey] = {
        count: entry.actionCount || 0,
        eventCount: entry.eventCount || 0,
        actions: [],
      };
    }
    return { days };
  }
  return null;
}

function normalizeYear(raw, year) {
  if (!raw) return null;
  if (raw.days && typeof raw.days === "object" && !Array.isArray(raw.days)) return raw;
  if (Array.isArray(raw)) {
    const days = {};
    const yearStart = new Date(Date.UTC(year, 0, 1));
    for (const entry of raw) {
      const dayDate = new Date(yearStart.getTime() + (entry.day - 1) * 24 * 60 * 60 * 1000);
      const dateKey = format(dayDate, "yyyy-MM-dd");
      days[dateKey] = entry.count || 0;
    }
    return { days };
  }
  return null;
}

function TimelinePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "day") {
        const res = await EventsService.getTimelineDay(format(selectedDate, "yyyy-MM-dd"));
        setData(normalizeDay(res));
      } else if (tab === "week") {
        const ws = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const res = await EventsService.getTimelineWeek(format(ws, "yyyy-MM-dd"));
        setData(normalizeWeek(res, ws));
      } else if (tab === "month") {
        const y = selectedDate.getFullYear();
        const m = selectedDate.getMonth() + 1;
        const res = await EventsService.getTimelineMonth(y, m);
        setData(normalizeMonth(res, y, m));
      } else if (tab === "year") {
        const y = selectedDate.getFullYear();
        const res = await EventsService.getTimelineYear(y);
        setData(normalizeYear(res, y));
      }
    } catch (err) {
      console.error("Timeline fetch error:", err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tab, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateDate = (dir) => {
    setSelectedDate((prev) => {
      if (tab === "day") return dir > 0 ? addDays(prev, 1) : subDays(prev, 1);
      if (tab === "week") return dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1);
      if (tab === "month") return dir > 0 ? addMonths(prev, 1) : subMonths(prev, 1);
      return new Date(prev.getFullYear() + dir, 0, 1);
    });
  };

  const dateLabel = useMemo(() => {
    if (tab === "day") return format(selectedDate, "EEEE, MMMM d, yyyy");
    if (tab === "week") {
      const ws = startOfWeek(selectedDate, { weekStartsOn: 1 });
      return t("timeline.weekOf", { date: format(ws, "MMM d, yyyy") });
    }
    if (tab === "month") return format(selectedDate, "MMMM yyyy");
    return format(selectedDate, "yyyy");
  }, [tab, selectedDate, t]);

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a id="sidebar-toggle" href="#" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }}>
              <i className="fas fa-bars"></i>
            </a>
            <h5>{t("timeline.title")}</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div className="timeline-tabs">
          {["day", "week", "month", "year"].map((key) => (
            <button key={key} className={`timeline-tab ${tab === key ? "timeline-tab-active" : ""}`} onClick={() => setTab(key)}>
              {t(`timeline.${key}`)}
            </button>
          ))}
        </div>

        <div className="timeline-nav">
          <button className="timeline-nav-btn" onClick={() => navigateDate(-1)}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className="timeline-nav-label">{dateLabel}</span>
          <button className="timeline-nav-btn" onClick={() => navigateDate(1)}>
            <i className="fas fa-chevron-right"></i>
          </button>
          {tab === "day" && (
            <button className="timeline-nav-btn timeline-today-btn" onClick={() => setSelectedDate(new Date())}>
              {t("common.today")}
            </button>
          )}
        </div>

        {loading && (
          <div className="d-flex justify-content-center" style={{ padding: 60 }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t("common.loading")}</span>
            </div>
          </div>
        )}

        {!loading && tab === "day" && <DayView data={data} />}
        {!loading && tab === "week" && <WeekView data={data} selectedDate={selectedDate} />}
        {!loading && tab === "month" && (
          <MonthView data={data} selectedDate={selectedDate} onDayClick={(d) => { setSelectedDate(d); setTab("day"); }} />
        )}
        {!loading && tab === "year" && <YearView data={data} year={selectedDate.getFullYear()} />}
      </div>
    </>
  );
}

function DayView({ data }) {
  const { t } = useTranslation();

  if (!data || !data.hours) {
    return (
      <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>
        {t("timeline.noActivityDay")}
      </p>
    );
  }

  const hasAny = Object.values(data.hours).some((h) => h.length > 0);
  if (!hasAny) {
    return (
      <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 40 }}>
        {t("timeline.noActivityDay")}
      </p>
    );
  }

  const actionColorCache = {};
  let actionColorIdx = 0;
  const getActionColor = (actionName) => {
    if (!actionColorCache[actionName]) {
      actionColorCache[actionName] = actionsColorMap[actionColorIdx % actionsColorMap.length];
      actionColorIdx++;
    }
    return actionColorCache[actionName];
  };

  const eventColorCache = {};
  let eventColorIdx = 0;
  const getEventColor = (eventName) => {
    if (!eventColorCache[eventName]) {
      eventColorCache[eventName] = actionsColorMap[eventColorIdx % actionsColorMap.length];
      eventColorIdx++;
    }
    return eventColorCache[eventName];
  };

  // Pre-scan all hours to assign stable colors
  for (const hour of HOURS) {
    for (const item of (data.hours[hour] || [])) {
      getEventColor(item.eventName || t("common.unknown"));
      getActionColor(item.actionName || "Action");
    }
  }

  return (
    <div className="day-timeline">
      {HOURS.map((hour) => {
        const hourData = data.hours[hour] || [];
        if (hourData.length === 0) {
          return (
            <div key={hour} className="day-timeline-row">
              <div className="day-timeline-hour">{String(hour).padStart(2, "0")}:00</div>
              <div className="day-timeline-track">
                <div className="day-timeline-line" />
              </div>
            </div>
          );
        }

        const grouped = {};
        for (const item of hourData) {
          const en = item.eventName || t("common.unknown");
          if (!grouped[en]) grouped[en] = [];
          grouped[en].push(item);
        }

        return (
          <div key={hour} className="day-timeline-row">
            <div className="day-timeline-hour">{String(hour).padStart(2, "0")}:00</div>
            <div className="day-timeline-track">
              <div className="day-timeline-line" />
              <div className="day-timeline-events">
                {Object.entries(grouped).map(([eventName, items]) => {
                  const evColor = getEventColor(eventName);
                  return (
                    <div
                      key={eventName}
                      className="day-timeline-event-card"
                      style={{
                        backgroundColor: `${evColor}15`,
                        borderColor: `${evColor}40`,
                      }}
                    >
                      <span className="day-timeline-event-name" style={{ color: evColor }}>{eventName}</span>
                      <div className="day-timeline-pills">
                        {items.map((action, i) => {
                          const pillColor = getActionColor(action.actionName || "Action");
                          const exactTime = action.time ? new Date(action.time).toLocaleTimeString() : "";
                          return (
                            <span
                              key={i}
                              className="day-timeline-pill"
                              style={{ backgroundColor: pillColor }}
                            >
                              {action.actionName || "Action"}
                              <span className="day-timeline-pill-time">{exactTime}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekView({ data, selectedDate }) {
  const { t } = useTranslation();
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const maxCount = useMemo(() => {
    if (!data || !data.grid) return 1;
    let max = 1;
    Object.values(data.grid).forEach((hours) => {
      Object.values(hours).forEach((count) => {
        if (count > max) max = count;
      });
    });
    return max;
  }, [data]);

  return (
    <div className="week-heatmap-container">
      <div className="week-heatmap">
        <div className="week-heatmap-corner" />
        {days.map((d, i) => (
          <div key={i} className="week-heatmap-day-label">
            {format(d, "EEE")}<span>{format(d, "d")}</span>
          </div>
        ))}
        {HOURS.map((hour) => (
          <React.Fragment key={hour}>
            <div className="week-heatmap-hour-label">{String(hour).padStart(2, "0")}</div>
            {days.map((d, dayIdx) => {
              const dayKey = format(d, "yyyy-MM-dd");
              const count = data?.grid?.[dayKey]?.[hour] || 0;
              const opacity = count > 0 ? 0.2 + (count / maxCount) * 0.8 : 0;
              return (
                <div
                  key={dayIdx}
                  className="week-heatmap-cell"
                  style={{ backgroundColor: count > 0 ? `rgba(10, 132, 255, ${opacity})` : "rgba(255,255,255,0.03)" }}
                  title={`${format(d, "EEE d")} ${hour}:00 — ${t("timeline.actionCount", { count, plural: count !== 1 ? "s" : "" })}`}
                >
                  {count > 0 && <span className="week-heatmap-count">{count}</span>}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function MonthView({ data, selectedDate, onDayClick }) {
  const { t } = useTranslation();
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const daysInMonth = getDaysInMonth(selectedDate);
  const firstDayOfWeek = getDay(startOfMonth(selectedDate));
  const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  const dayNames = [t("weekdays.mon"), t("weekdays.tue"), t("weekdays.wed"), t("weekdays.thu"), t("weekdays.fri"), t("weekdays.sat"), t("weekdays.sun")];

  return (
    <div className="month-calendar">
      <div className="month-calendar-header">
        {dayNames.map((d) => <div key={d} className="month-calendar-dayname">{d}</div>)}
      </div>
      <div className="month-calendar-grid">
        {Array.from({ length: offset }).map((_, i) => (
          <div key={`empty-${i}`} className="month-calendar-cell month-calendar-cell-empty" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const dayNum = i + 1;
          const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
          const dayData = data?.days?.[dateKey];
          const count = dayData?.count || dayData?.actionCount || 0;
          const isToday = isSameDay(new Date(year, month, dayNum), new Date());

          return (
            <div
              key={dayNum}
              className={`month-calendar-cell ${isToday ? "month-calendar-cell-today" : ""} ${count > 0 ? "month-calendar-cell-active" : ""}`}
              onClick={() => onDayClick(new Date(year, month, dayNum))}
            >
              <span className="month-calendar-date">{dayNum}</span>
              {count > 0 && <span className="month-calendar-count">{count}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ data, year }) {
  const { t } = useTranslation();
  const yearStart = startOfYear(new Date(year, 0, 1));
  const yearEnd = endOfYear(new Date(year, 0, 1));
  const allDays = eachDayOfInterval({ start: yearStart, end: yearEnd });

  const maxCount = useMemo(() => {
    if (!data?.days) return 1;
    let max = 1;
    Object.values(data.days).forEach((val) => {
      const count = typeof val === "number" ? val : val?.count || 0;
      if (count > max) max = count;
    });
    return max;
  }, [data]);

  const weeks = useMemo(() => {
    const cols = [];
    let currentWeek = [];
    const firstDayOfWeek = getDay(yearStart);
    const startPad = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < startPad; i++) currentWeek.push(null);
    allDays.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) { cols.push(currentWeek); currentWeek = []; }
    });
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      cols.push(currentWeek);
    }
    return cols;
  }, [allDays, yearStart]);

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((week, weekIdx) => {
      const firstDay = week.find((d) => d !== null);
      if (firstDay) {
        const m = firstDay.getMonth();
        if (m !== lastMonth) { labels.push({ weekIdx, label: format(firstDay, "MMM") }); lastMonth = m; }
      }
    });
    return labels;
  }, [weeks]);

  return (
    <div className="year-heatmap-container">
      <div className="year-heatmap-months">
        {monthLabels.map((ml) => (
          <span key={ml.weekIdx} className="year-heatmap-month-label" style={{ gridColumnStart: ml.weekIdx + 1 }}>
            {ml.label}
          </span>
        ))}
      </div>
      <div className="year-heatmap">
        <div className="year-heatmap-day-labels">
          {[t("weekdays.mon"), "", t("weekdays.wed"), "", t("weekdays.fri"), "", ""].map((d, i) => (
            <span key={i} className="year-heatmap-day-label">{d}</span>
          ))}
        </div>
        <div className="year-heatmap-grid">
          {weeks.map((week, wi) => (
            <div key={wi} className="year-heatmap-col">
              {week.map((day, di) => {
                if (!day) return <div key={di} className="year-heatmap-cell year-heatmap-cell-empty" />;
                const dateKey = format(day, "yyyy-MM-dd");
                const val = data?.days?.[dateKey];
                const count = typeof val === "number" ? val : val?.count || 0;
                const intensity = count > 0 ? 0.15 + (count / maxCount) * 0.85 : 0;
                return (
                  <div
                    key={di}
                    className="year-heatmap-cell"
                    style={{ backgroundColor: count > 0 ? `rgba(30, 142, 58, ${intensity})` : "rgba(255,255,255,0.04)" }}
                    title={`${format(day, "MMM d, yyyy")}: ${t("timeline.actionCount", { count, plural: count !== 1 ? "s" : "" })}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TimelinePage;
